<template>
    <div class="nav-bar-wrapper">
        <div class="nav-zone" ref="navZone" @mouseleave="_resetPeek">
            <div class="nav-new-left">
                <div class="nav-new-icons" :class="{ collapsed: !showLabels }">
                    <div
                            v-for="option in items"
                            :key="option.name"
                            :ref="(el) => { if (el) iconRefs[option.name] = el; else delete iconRefs[option.name]; }"
                            class="nav-new-icon-btn"
                            :class="{ active: isActive(option), disabled: option.disabled }"
                            @click="handleClick($event, option)"
                            @mouseenter="onIconEnter(option.name)"
                    >
                        <Icon
                                :name="option.icon?.name"
                                class="nav-toggle"
                                :class="option.icon?.name"
                                size="xl"
                        />
                        <span v-show="showLabels" class="nav-label">{{ option.label }}</span>
                    </div>

                    <div class="nav-new-icons__show-hide labels">
                        <Icon
                                name="arrow-right-to-line"
                                class="nav-toggle"
                                :class="{ 'labels-visible': showLabels, rotated: !showLabels }"
                                size="xl"
                                :color="'var(--text-color-subtitle)'"
                                @click="$emit('toggle-labels')"
                        />
                    </div>
                </div>
            </div>

            <div
                    class="vms-nav-drawer-region"
                    :class="{ 'open': isPinned }"
            >
                <nav-drawer
                        :nav-item="drawerNavItem"
                        :show-grid-controls="showGridControls"
                        @close="closeDrawer"
                        @commit="onCommit"
                >
                    <!-- TODO: Make adminItems, toolsItems generic when connected to navigationStore -->
                    <template v-if="drawerNavItem?.name === 'ADMIN_OPT' || drawerNavItem?.name === 'M_TOOLS'" #drawer-content>
                        <div v-if="drawerNavItem?.name === 'ADMIN_OPT'" class="drawer-admin-list">
                            <button
                                    v-for="item in adminItems"
                                    :key="item.value"
                                    class="drawer-admin-item"
                                    @click="$emit('action', { item })"
                            >{{ item.text }}</button>
                        </div>
                        <div v-else-if="drawerNavItem?.name === 'M_TOOLS'" class="drawer-admin-list">
                            <button
                                    v-for="item in toolsItems"
                                    :key="item.value"
                                    class="drawer-admin-item"
                                    @click="$emit('action', { item })"
                            >{{ item.text }}</button>
                        </div>
                    </template>
                </nav-drawer>
            </div>
            <nav-floating
                    :nav-item="floatingNavItem"
                    :triangle-top="triangleTop"
                    :visible="showFloating"
                    :left="navStripWidth"
                    @pin="onPin"
                    @commit="onCommit"
            >
                <template v-if="floatingNavItem?.name === 'ADMIN_OPT' || floatingNavItem?.name === 'M_TOOLS'" #floating-content>
                    <div v-if="floatingNavItem?.name === 'ADMIN_OPT'" class="drawer-admin-list">
                        <button
                                v-for="item in adminItems"
                                :key="item.value"
                                class="drawer-admin-item"
                                @click="$emit('action', { item })"
                        >{{ item.text }}</button>
                    </div>
                    <div v-else-if="floatingNavItem?.name === 'M_TOOLS'" class="drawer-admin-list">
                        <button
                                v-for="item in toolsItems"
                                :key="item.value"
                                class="drawer-admin-item"
                                @click="$emit('action', { item })"
                        >{{ item.text }}</button>
                    </div>
                </template>
            </nav-floating>
        </div><!-- /.nav-zone -->
    </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import Icon from '@common/icon/Icon.vue';
import NavDrawer from '@common/navigation/NavDrawer.vue';

const PEEK_WARMUP_MS = 500;

export default {
    name: 'NavBar',
    components: {
        Icon,
        'nav-drawer': NavDrawer,
        'nav-floating': defineAsyncComponent(() => import('@common/navigation/NavFloating.vue')),
    },
    props: {
        items: {
            type: Array,
            default: () => [],
        },
        activeItem: {
            type: String,
            default: null,
        },
        showLabels: {
            type: Boolean,
            default: false,
        },
        adminItems: {
            type: Array,
            default: () => [],
        },
        toolsItems: {
            type: Array,
            default: () => [],
        },
    },
    emits: ['navigate', 'toggle-labels', 'commit', 'action'],
    data() {
        return {
            hoveredNavButton: null,
            peekMode: false,
            isPinned: false,
            triangleTop: 0,
            navStripWidth: 0,
        };
    },
    created() {
        this.iconRefs = {};
    },
    computed: {
        drawerNavItem() {
            const name = this.isPinned ? this.activeItem : (this.hoveredNavButton || this.activeItem);
            if (!name) return null;
            return this.items.find((item) => item.name === name) || null;
        },
        showGridControls() {
            if (!this.drawerNavItem?.isGrid) return false;
            if (this.hoveredNavButton && this.hoveredNavButton !== this.activeItem) return false;
            return true;
        },
        floatingNavItem() {
            if (!this.hoveredNavButton) return null;
            return this.items.find((item) => item.name === this.hoveredNavButton) || null;
        },
        showFloating() {
            if (!this.peekMode || !this.hoveredNavButton) return false;
            if (this.isPinned && this.hoveredNavButton === this.activeItem) return false;
            return true;
        },
    },
    mounted() {
        // This helps the floating nav position correct relative to if icon labels are visible/hidden
        const navLeft = this.$refs.navZone?.querySelector('.nav-new-left');
        if (navLeft) {
            this._resizeObserver = new ResizeObserver(() => {
                requestAnimationFrame(() => this._updateNavStripWidth());
            });
            this._resizeObserver.observe(navLeft);
        }
    },
    methods: {
        isActive(option) {
            return option.name === this.activeItem;
        },
        handleClick(event, option) {
            if (option.disabled) return;
            this.$emit('navigate', option);
            this._resetPeek();
        },
        onIconEnter(name) {
            this.hoveredNavButton = name;
            this._computeTriangleTop(name);
            if (this.peekMode) return;
            if (!this._warmupTimer) {
                this._warmupTimer = setTimeout(() => {
                    this.peekMode = true;
                    this._warmupTimer = null;
                }, PEEK_WARMUP_MS);
            }
        },
        _updateNavStripWidth() {
            const navLeft = this.$refs.navZone?.querySelector('.nav-new-left');
            if (navLeft) this.navStripWidth = navLeft.offsetWidth;
        },
        _computeTriangleTop(name) {
            // uses elem ref to figure out where the triangle should be positioned
            const iconEl = this.iconRefs[name];
            const zoneEl = this.$refs.navZone;
            if (!iconEl || !zoneEl) return;
            const iconRect = iconEl.getBoundingClientRect();
            const zoneRect = zoneEl.getBoundingClientRect();
            this.triangleTop = iconRect.top - zoneRect.top + iconRect.height / 2;
        },
        _resetPeek() {
            this.hoveredNavButton = null;
            this.peekMode = false;
            clearTimeout(this._warmupTimer);
            this._warmupTimer = null;
        },
        onPin() {
            this.isPinned = true;
            this._resetPeek();
        },
        closeDrawer() {
            this.isPinned = false;
            this._resetPeek();
        },
        onCommit(navItem) {
            this.$emit('commit', navItem);
        },
    },
    beforeUnmount() {
        this._resizeObserver?.disconnect();
        clearTimeout(this._warmupTimer);
    },
};
</script>

<style scoped>
.nav-bar-wrapper {
    display: contents;
}

.nav-zone {
    --nav-panel-width: 220px;
    position: relative;
    display: flex;
    height: 100%;
}

.nav-new-left {
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: var(--surface-default);
    transition: width var(--transition-default) ease-in-out, background-color var(--transition-default) ease-in-out;
}

.nav-new-left .nav-new-icons.collapsed .nav-label {
    opacity: 0;
    transform: translateX(-10px);
    pointer-events: none;
}

.nav-new-icon-btn {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: var(--space-6);
}

.nav-new-icons.collapsed .nav-new-icon-btn {
    justify-content: center;
}

.nav-new-icon-btn:hover {
    background-color: var(--primary-50);
    color: var(--text-color-body);
}

.nav-new-icon-btn:focus-visible {
    outline: 2px solid var(--border-color-default-primary);
    outline-offset: -2px;
}

.nav-new-icon-btn.active {
    background-color: var(--surface-default-primary);
    color: var(--text-color-inverse);
    font-weight: var(--text-weight-medium);
}

.nav-new-icon-btn.active i {
    color: var(--text-color-inverse) !important;
}

.nav-new-icon-btn.disabled {
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
}

.nav-new-icons {
    display: flex;
    flex-direction: column;
    align-items: baseline;
    flex-grow: 1;
    width: 100%;
    padding-bottom: var(--space-4);
    transition: transform var(--transition-3) cubic-bezier(0.4, 0, 0.2, 1);
}

.nav-new-icons__show-hide {
    display: flex;
    padding: 0 var(--space-8);
    margin-top: auto;
    height: fit-content;
    width: 100%;
    justify-content: flex-start;
}

.nav-new-left i.nav-toggle {
    transition: transform var(--transition-3) ease;
}

.nav-new-left i.nav-toggle.rotated {
    transform: rotate(-180deg);
}

.nav-new-icons > div {
    width: 100%;
}

.nav-new-icon-btn span.nav-label {
    font-size: var(--text-size-base);
    padding: 0 var(--space-6);
    white-space: nowrap;
    max-width: 120px;
    display: inline-block;
    opacity: 1;
    transform: translateX(0);
    transition: opacity var(--transition-default) ease, transform var(--transition-default) ease;
}

.vms-nav-drawer-region {
    max-width: 0;
    overflow: hidden;
    transition: max-width var(--transition-default) ease;
    background-color: var(--surface-default);
    border-left: 1px solid var(--border-color-default);
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.06);
}

.vms-nav-drawer-region.open {
    max-width: var(--nav-panel-width);
}

.drawer-admin-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
}

.drawer-admin-item {
    display: flex;
    align-items: center;
    width: 100%;
    padding: var(--space-4) var(--space-5);
    border: none;
    background: none;
    cursor: pointer;
    text-align: left;
    font-size: var(--text-size-base);
    color: var(--text-color-title);
    border-radius: var(--radius-xs);
    transition: background-color var(--transition-default) ease;
}

.drawer-admin-item:hover {
    background-color: var(--surface-subtle);
}

.drawer-admin-item:focus-visible {
    outline: 2px solid var(--border-color-default-primary);
    outline-offset: -2px;
}
</style>
